import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BosDurum,
  Buton,
  cn,
  DenemeNetGrafigi,
  denemeNoktalari,
  FiltreKarti,
  Iskelet,
  Kart,
} from "@koclukpanel/ui";
import { SayfaBasligiEki } from "./Kabuk";
import { DenemeListesiTanim } from "./DenemeTanim";
import { DenemeDisaAktar } from "./DenemeDisaAktar";
import { OgrenciSecimAlani } from "./OgrenciSecici";
import {
  ApiHatasi,
  denemeSonucu,
  denemeTrendi,
  telafiOnerisi,
  type DenemeSonucu,
  type GirisYontemi,
  type DenemeTrendi,
  type TelafiOnerisi,
} from "./api";

// F-EXM-24 — sunucunun türettiği durum liste ve ayrıntıda aynı metinle gösterilir.
type DuzeltmeliTrend = Omit<DenemeTrendi, "denemeler"> & {
  denemeler: Array<DenemeTrendi["denemeler"][number] & { duzeltmeBekliyor?: true }>;
};
type DuzeltmeliSonuc = DenemeSonucu & { duzeltmeBekliyor?: true };

interface DenemelerProps {
  ogrenciId: string;
  ogrenciAdi: string;
  onGeri: () => void;
  /** Telafi konularını program editörüne taşır (F-EXM-10) */
  onTelafiyeAktar?: (konular: TelafiOnerisi["konular"]) => void;
  /** Filtre kartından başka öğrenci seçildi (sürüm 9a L2). */
  onOgrenciSec?: (id: string, ad: string) => void;
  /** Öğrenci seçimini bırakıp deneme tablosuna dön. */
  onTumDenemeler?: () => void;
}

/**
 * DENEME SINAVLARI GİRİŞİ — UNİKOÇ "Deneme Sınavları" (sürüm 9a L2; DenemeSinavi-Index)
 *
 * Eski panel açılışta DENEME TABLOSUNU gösteriyordu (Ad · PDF · katılım · tarihler ·
 * durum · Detaylar), öğrenci filtre kartındaydı. Burada aynı tablo "Deneme tanımla"
 * sekmesinin bileşeni (`GET /denemeler/sayfali`; kapı ve kapsam sunucuda) ve filtre
 * kartına öğrenci seçici ekleniyor: öğrenci seçilince o öğrencinin analizi açılır.
 *
 * Liste ucu `deneme.sinav.yonet` ya da `deneme.sonuc.gir` istiyor; ikisi de yoksa
 * tablo ÇİZİLMİYOR (403 alacak bir istek atmak yerine) ve yalnız öğrenci seçimi kalıyor.
 */
export function DenemeGirisi({
  listeGorebilir,
  yonetebilir,
  duzenleyebilir = false,
  atayabilir,
  onOgrenciSec,
}: {
  listeGorebilir: boolean;
  yonetebilir: boolean;
  duzenleyebilir?: boolean;
  atayabilir: boolean;
  onOgrenciSec: (id: string, ad: string) => void;
}) {
  const ogrenciAlani = (
    <OgrenciSecimAlani
      secili={null}
      onSec={onOgrenciSec}
      yardim="Seçtiğinizde öğrencinin deneme analizi açılır."
    />
  );
  if (listeGorebilir) {
    return (
      <DenemeListesiTanim
        yonetebilir={yonetebilir}
        duzenleyebilir={duzenleyebilir}
        atayabilir={atayabilir}
        filtreEki={ogrenciAlani}
      />
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <SayfaBasligiEki aciklama="Öğrenci seçerek deneme sonuçlarını ve net değişimini görün." />
      <FiltreKarti baslik="Filtreler" ikon="filtre" sutun={2}>
        {ogrenciAlani}
      </FiltreKarti>
      <BosDurum
        ikon="deneme"
        baslik="Öğrenci seçin"
        aciklama="Deneme analizi bir öğrenciye ait; yukarıdan öğrenci seçtiğinizde sonuçlar burada görünür."
      />
    </div>
  );
}

/**
 * DENEME ANALIZI VE TELAFI — F-EXM-09/10
 *
 * ================================================================
 * TURKIYE GENELI SIRALAMA YOK
 * ================================================================
 * Sartnamenin acik kurali (F-EXM-06): "Türkiye geneli sıralama
 * verilmez — o veri yayınevindedir." Bu ekranda yalnizca KURUM ICI
 * sira var ve kac kisi arasinda oldugu da yaziyor: "3 kisi arasinda
 * 1." ile "300 kisi arasinda 1." ayni sey degil.
 */
export function Denemeler({
  ogrenciId,
  ogrenciAdi,
  onGeri,
  onTelafiyeAktar,
  onOgrenciSec,
  onTumDenemeler,
}: DenemelerProps) {
  const [acikAtama, setAcikAtama] = useState<string | null>(null);

  /* UNİKOÇ filtre kartı: öğrenci burada değişir; "Tüm denemeler" tabloya döner. */
  const filtre = (
    <FiltreKarti
      baslik="Filtreler"
      ikon="filtre"
      sutun={2}
      eylemler={
        <>
          {onTumDenemeler && (
            <Buton gorunum="kesikli" onClick={onTumDenemeler}>
              Tüm denemeler
            </Buton>
          )}
          <Buton gorunum="sessiz" onClick={onGeri}>
            ← İş listesi
          </Buton>
        </>
      }
    >
      <OgrenciSecimAlani
        secili={ogrenciId}
        seciliAd={ogrenciAdi}
        onSec={(id, ad) => onOgrenciSec?.(id, ad)}
      />
    </FiltreKarti>
  );

  const { data, isPending, isError, error, refetch } = useQuery<DuzeltmeliTrend>({
    queryKey: ["deneme-trendi", ogrenciId],
    queryFn: () => denemeTrendi(ogrenciId),
  });

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Denemeler alınamadı"
        aciklama={error instanceof ApiHatasi ? error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  if (data.denemeler.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {filtre}
        <BosDurum
          baslik="Sonuçlanmış deneme yok"
          aciklama={`${ogrenciAdi} için henüz sonuç girilmiş bir deneme bulunmuyor.`}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <SayfaBasligiEki
        aciklama={`${ogrenciAdi} — deneme sonuçları, net değişimi ve telafi önerileri.`}
      />
      {filtre}

      <TrendOzeti trend={data} />

      {/*
        Dalga 3 — tür bazlı net çizgisi. F-EXM-09: hedef çizgisi, özet cümlesi ve son denemenin ders bazlı farkı
        sunucudan gelen `hedefNet`ten (grafik hesaplamaz); hedef girilmemişse eski not.
      */}
      <DenemeNetGrafigi denemeler={denemeNoktalari(data.denemeler)} />

      <Kart baslik="Denemeler">
        <ul className="flex flex-col gap-2">
          {[...data.denemeler].reverse().map((d, i, ters) => (
            <li key={d.examId}>
              <button
                type="button"
                onClick={() => setAcikAtama(acikAtama === d.atamaId ? null : d.atamaId)}
                aria-expanded={acikAtama === d.atamaId}
                className={cn(
                  "min-h-dokunma rounded-alan flex w-full items-center gap-3 px-3 text-left",
                  "bg-notr-50 hover:bg-notr-100",
                  "focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                )}
              >
                <span className="min-w-0 flex-1">
                  <span className="text-notr-900 block truncate text-sm font-medium">{d.ad}</span>
                  <span className="text-notr-600 block text-xs">
                    {new Date(d.tarih).toLocaleDateString("tr-TR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}{" "}
                    · {d.tur} · {siraMetni(d.rank, d.percentile)}
                  </span>
                  {/*
                    ================================================================
                    GİRİŞ YÖNTEMİ — ÇİZİMDEKİ "Giriş yöntemi" SÜTUNU
                    ================================================================
                    Tuval her satırda "Cevap cevap" / "Ders bazlı net"
                    yazıyor ve hızlının yanına "konu analizi yok" notunu
                    koyuyor. Sebebi listeye bakan uzmanın kararı: konu
                    bazlı telafi planlayacaksa hangi denemeye
                    güvenebileceğini satırı AÇMADAN bilmesi gerekiyor.

                    YALNIZCA HIZLI YÖNTEM İŞARETLENİYOR. "Cevap cevap"
                    beklenen hâl ve her satıra yazmak, asıl anlamlı olan
                    eksikliği gürültüye gömerdi.
                  */}
                  {d.duzeltmeBekliyor && (
                    <span className="text-uyari-600 block text-xs">Düzeltiliyor</span>
                  )}
                  {d.girisYontemi === "DERS_NET" && (
                    <span className="text-uyari-600 mt-0.5 block text-xs">
                      Ders bazlı net · konu analizi yok
                    </span>
                  )}
                </span>
                <span className="flex shrink-0 flex-col items-end">
                  <span className="text-notr-900 text-sm font-semibold tabular-nums">
                    {yuvarla(d.net)} net
                  </span>
                  {/*
                    ================================================================
                    DEĞİŞİM SÜTUNU — ÇİZİMDEN (`DenemelerVeAbonelik.dc.html`)
                    ================================================================
                    Tuvalde her satırda "+6,25 / −3,50" duruyor ve trend
                    kartındaki tek "son değişim" sayısı bunun yerini
                    tutmuyor: uzmanın aradığı şey "hangi denemeden sonra
                    düştü", yani satır satır fark.

                    Liste ters çevrilmiş (yeni üstte), o yüzden bir
                    ÖNCEKİ deneme dizide bir SONRAKİ eleman.

                    EN ESKİ SATIRDA "0" DEĞİL HİÇBİR ŞEY: kıyaslanacak
                    önceki deneme yok. "0 değişim" yazmak, ölçülmemiş bir
                    şeyi ölçülmüş göstermek olurdu — trend kartındaki
                    `sonDegisim` için de aynı gerekçe yazılı.

                    Renk TEK BAŞINA anlam taşımıyor: işaret (+/−) her
                    zaman yazılı.
                  */}
                  {(() => {
                    const onceki = ters[i + 1];
                    if (!onceki) return null;
                    const fark = d.net - onceki.net;
                    return (
                      <span
                        className={cn(
                          "text-xs tabular-nums",
                          fark >= 0 ? "text-onay-700" : "text-uyari-600",
                        )}
                      >
                        {fark >= 0 ? "+" : "−"}
                        {yuvarla(Math.abs(fark))}
                      </span>
                    );
                  })()}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </Kart>

      {acikAtama && (
        <>
          <SonucAyrintisi atamaId={acikAtama} />
          <TelafiPaneli
            atamaId={acikAtama}
            /*
              Yöntem AÇILAN SATIRIN kendisinden geliyor. `??` dalı
              teoride ölü — açık atama listeden seçiliyor — ama tip
              güvenliği için gerekiyor ve varsayılanı `CEVAP_CEVAP`
              yapmak yanlış olurdu: bulunamayan bir satır için
              "ölçüldü" demek, tam da kaçındığımız iddia.
            */
            girisYontemi={
              data.denemeler.find((d) => d.atamaId === acikAtama)?.girisYontemi ?? "DERS_NET"
            }
            onAktar={onTelafiyeAktar}
          />
        </>
      )}
    </div>
  );
}

function TrendOzeti({ trend }: { trend: DenemeTrendi }) {
  return (
    <Kart baslik="Trend">
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Olcum etiket="Deneme" deger={String(trend.denemeler.length)} />
        <Olcum
          etiket="Son değişim"
          /*
            TEK DENEMEDE "0" DEGIL "—".
            "0 degisim" demek, degisim OLCULDU ve sifir cikti demektir;
            oysa kiyaslanacak onceki deneme yok.
          */
          deger={
            trend.sonDegisim === null
              ? "—"
              : `${trend.sonDegisim > 0 ? "+" : ""}${yuvarla(trend.sonDegisim)}`
          }
          alt={trend.sonDegisim === null ? "tek deneme" : undefined}
          vurgu={trend.sonDegisim === null ? undefined : trend.sonDegisim >= 0 ? "iyi" : "kotu"}
        />
        <Olcum etiket="Ortalama" deger={trend.ortalama === null ? "—" : yuvarla(trend.ortalama)} />
        <Olcum etiket="En yüksek" deger={trend.enYuksek === null ? "—" : yuvarla(trend.enYuksek)} />
      </dl>
    </Kart>
  );
}

/**
 * TEK TUSLA TELAFI — F-EXM-10, "kilit ozellik".
 *
 * Bu panel PROGRAM URETMIYOR, oneri gosteriyor ve uzmanin onayiyla
 * program editorune aktariyor. Kendiliginden uretseydi uzmanin
 * onaylamadigi gorevler ogrencinin programina duserdi; program
 * uzmanla ogrencinin birlikte kurdugu bir sey (F-SCH).
 */
function TelafiPaneli({
  atamaId,
  girisYontemi,
  onAktar,
}: {
  atamaId: string;
  /**
   * Sonucun nasıl girildiği — boş listenin SEBEBİNİ bu belirliyor.
   *
   * Panel bu bilgiyi kendi çıkaramaz: `TelafiOnerisi` yalnızca konu
   * listesini döndürüyor ve boş bir liste iki apayrı şeyin sonucu
   * olabiliyor — "yanlış yok" ve "hiç ölçülmedi".
   */
  girisYontemi: GirisYontemi;
  onAktar?: (konular: TelafiOnerisi["konular"]) => void;
}) {
  const [secili, setSecili] = useState<Set<string>>(new Set());

  const { data, isPending, isError, error } = useQuery<TelafiOnerisi>({
    queryKey: ["telafi", atamaId],
    queryFn: () => telafiOnerisi(atamaId),
    retry: false,
  });

  if (isPending) return <p className="text-notr-600 text-sm">Telafi önerisi hazırlanıyor…</p>;

  if (isError) {
    return (
      <p role="alert" className="rounded-alan bg-hata-500/10 text-hata-700 px-3 py-2 text-sm">
        {error instanceof ApiHatasi ? error.message : "Telafi önerisi alınamadı."}
      </p>
    );
  }

  if (data.konular.length === 0) {
    return (
      /*
        ================================================================
        "KONU BAZLI YANLIŞ ÇIKMADI" TEK BAŞINA YANLIŞ BİR CÜMLEYDİ
        ================================================================
        Eski metin iki sebep sayıyordu: yanlış yok, ya da cevap
        anahtarında konu etiketi yok. ÜÇÜNCÜ ve en sık sebebi
        atlıyordu: sonuç DERS BAZLI NET yöntemiyle girildiyse
        `rawAnswers` boş kalıyor ve konu analizi HİÇ ÜRETİLMİYOR —
        bu bilinçli bir karar ve `deneme.service.ts` içinde yazılı
        ("üretilseydi, olmayan veriden çıkarım yapmış olurduk").

        Fark küçük değil: "yanlış çıkmadı" uzmana öğrencinin o konuları
        BİLDİĞİNİ söylüyor, oysa doğrusu "elimizde veri yok". Bu
        deponun her yerinde tekrarlanan kural bu — ölçülmedi ≠ sıfır.

        Bir tur önce ekran ayrımı YAPAMIYORDU — yöntem ne
        `DenemeTrendi` ne `DenemeSonucu` içinde dönüyordu — ve cümle üç
        ihtimali birden sayıyordu. Sunucu artık `girisYontemi`
        döndürüyor: hangi durumda olduğumuz ÖLÇÜLMÜŞ bir veri.
        `DERS_NET` ise cevap kesin; ötekinde hâlâ iki ihtimal var ve
        ikisi de "veri yok" tarafında, o yüzden ikisi de yazılıyor.
      */
      <p role="status" className="rounded-alan bg-notr-50 text-notr-600 px-3 py-2 text-sm">
        {girisYontemi === "DERS_NET" ? (
          <>
            Bu denemenin sonucu <strong>ders bazlı net</strong> girilmiş; konu analizi{" "}
            <strong>hiç üretilmedi</strong>. Konu bazlı telafi için sonucun cevap cevap girilmesi
            gerekiyor.
          </>
        ) : (
          <>
            Bu deneme için konu bazlı veri yok. Cevap anahtarına konu etiketi girilmemiş olabilir,
            ya da gerçekten konu bazlı yanlış çıkmamıştır.
          </>
        )}
      </p>
    );
  }

  const hepsi = data.konular.map((k) => k.topicId);
  const tumuSecili = secili.size === hepsi.length;

  return (
    <section className="rounded-kart shadow-kart border-marka-600/20 bg-marka-50/40 flex flex-col gap-3 border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-notr-900 text-sm font-semibold">Yanlış yapılan konular</h3>
        <Buton gorunum="sessiz" onClick={() => setSecili(tumuSecili ? new Set() : new Set(hepsi))}>
          {tumuSecili ? "Seçimi kaldır" : "Tümünü seç"}
        </Buton>
      </div>

      <ul className="flex flex-col gap-1.5">
        {data.konular.map((k) => (
          <li key={k.topicId}>
            <label className="min-h-dokunma rounded-alan bg-notr-0 flex cursor-pointer items-center gap-3 px-3">
              <input
                type="checkbox"
                checked={secili.has(k.topicId)}
                onChange={(e) =>
                  setSecili((m) => {
                    const y = new Set(m);
                    if (e.target.checked) y.add(k.topicId);
                    else y.delete(k.topicId);
                    return y;
                  })
                }
                className="size-5 shrink-0 rounded"
              />
              <span className="min-w-0 flex-1">
                <span className="text-notr-900 block truncate text-sm">{k.konuAdi}</span>
                <span className="text-notr-600 block text-xs">
                  {k.dersAdi ? `${k.dersAdi} · ` : ""}
                  {k.yanlis} yanlış · {basariMetni(k.basariOrani)}
                </span>
              </span>
            </label>
          </li>
        ))}
      </ul>

      <Buton
        disabled={secili.size === 0}
        onClick={() => onAktar?.(data.konular.filter((k) => secili.has(k.topicId)))}
      >
        {secili.size === 0 ? "Konu seçin" : `${secili.size} konuyu gelecek haftanın taslağına ekle`}
      </Buton>

      <p className="text-notr-600 text-xs">
        Program üretilmez; seçtiğiniz konular taslağa görev olarak eklenir ve yayınlamadan önce
        düzenleyebilirsiniz.
      </p>
    </section>
  );
}

function Olcum({
  etiket,
  deger,
  alt,
  vurgu,
}: {
  etiket: string;
  deger: string;
  alt?: string;
  vurgu?: "iyi" | "kotu";
}) {
  return (
    <div className="rounded-alan bg-notr-50 px-3 py-2">
      <dt className="text-notr-600 text-xs">{etiket}</dt>
      <dd
        className={cn(
          "text-xl font-semibold tabular-nums",
          vurgu === "iyi" && "text-onay-700",
          vurgu === "kotu" && "text-hata-500",
          !vurgu && "text-notr-900",
        )}
      >
        {deger}
      </dd>
      {alt && <dd className="text-notr-600 text-xs">{alt}</dd>}
    </div>
  );
}

/** Net ekranda yuvarlaniyor; kayitta TAM deger duruyor. */
function yuvarla(n: number): string {
  return String(Math.round(n * 100) / 100);
}

/**
 * Kurum ici sira.
 *
 * Yuzdelik `null` ise (tek katilimci) sira TEK BASINA gosteriliyor:
 * "%0'dasin" demek bir kiyaslama yapilmis izlenimi verirdi.
 */
function siraMetni(rank: number | null, percentile: number | null): string {
  if (rank == null) return "sıralanmadı";
  if (percentile == null) return `kurum içi ${rank}. (tek katılımcı)`;
  return `kurum içi ${rank}. · üst %${Math.round(percentile * 100)}`;
}

/** Olculemeyen basari "%0" DEGIL. */
function basariMetni(oran: number | null): string {
  if (oran === null) return "başarı ölçülemedi";
  return `%${Math.round(oran * 100)} başarı`;
}

/**
 * TEK DENEME SONUCU — F-EXM-09
 *
 * `GET /denemeler/sonuclar/:atamaId` yazılıydı, hiçbir ekran
 * çağırmıyordu. Listede yalnızca NET görünüyordu; doğru/yanlış/boş
 * dağılımı, puan ve süre hiçbir yerde yoktu. "40 net" ile "45 doğru
 * 20 yanlış" farklı çalışma önerileri gerektirir.
 *
 * ================================================================
 * SÜRE ŞÜPHELİYSE SAYIYI GÖSTERMİYORUZ
 * ================================================================
 * Sunucu `sureSupheli` ile "bu ölçüm güvenilir değil" diyor
 * (öğrenci sayacı açık unutmuş, uygulama arka planda kalmış).
 * Sayıyı yine de gösterip yanına küçük bir uyarı koymak, sayının
 * okunup uyarının okunmaması demekti. Ölçülemeyen bir şeyi
 * göstermemek, yanlış göstermekten iyi.
 */
function SonucAyrintisi({ atamaId }: { atamaId: string }) {
  const { data, isPending, isError, error } = useQuery<DuzeltmeliSonuc>({
    queryKey: ["deneme-sonucu", atamaId],
    queryFn: () => denemeSonucu(atamaId),
    retry: false,
  });

  if (isPending) return <Iskelet satir={3} />;

  if (isError) {
    return (
      <p role="alert" className="text-hata-700 text-sm font-medium">
        {error instanceof ApiHatasi ? error.message : "Sonuç ayrıntısı alınamadı."}
      </p>
    );
  }

  return (
    <Kart baslik={data.deneme.name}>
      <div className="flex flex-col gap-3">
        <DenemeDisaAktar denemeId={data.deneme.id} />
        {data.duzeltmeBekliyor && <p role="status">Düzeltiliyor</p>}
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {(
            [
              ["Doğru", data.toplam.dogru],
              ["Yanlış", data.toplam.yanlis],
              ["Boş", data.toplam.bos],
              ["Net", yuvarla(data.toplam.net)],
            ] as const
          ).map(([etiket, deger]) => (
            <div key={etiket} className="rounded-alan bg-notr-50 p-3">
              <dt className="text-notr-600 text-xs">{etiket}</dt>
              <dd className="text-notr-900 mt-0.5 text-lg font-semibold tabular-nums">{deger}</dd>
            </div>
          ))}
        </dl>

        <dl className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <div className="flex gap-2">
            <dt className="text-notr-600">Puan</dt>
            {/*
              `null` = puan HESAPLANMADI (deneme puan üretmiyor ya da
              katsayı tanımsız). "0" yazmak "sıfır puan aldı" demek
              olurdu ve ikisi ayrı şey.
            */}
            <dd className="text-notr-900 font-medium tabular-nums">
              {data.puan === null ? (
                <span className="text-notr-600 font-normal">hesaplanmadı</span>
              ) : (
                yuvarla(data.puan)
              )}
            </dd>
          </div>

          <div className="flex gap-2">
            <dt className="text-notr-600">Kurum içi sıra</dt>
            <dd className="text-notr-900 font-medium">{siraMetni(data.rank, data.percentile)}</dd>
          </div>

          <div className="flex gap-2">
            <dt className="text-notr-600">Süre</dt>
            <dd className="text-notr-900 font-medium">
              {data.sure === null ? (
                <span className="text-notr-600 font-normal">ölçülmedi</span>
              ) : data.sureSupheli ? (
                <span className="text-uyari-600 font-normal">ölçüm güvenilir değil</span>
              ) : (
                `${data.sure} dk`
              )}
            </dd>
          </div>
        </dl>
      </div>
    </Kart>
  );
}
